import {
  buildMetasearchDiscountRequestKey,
  isMetasearchDiscountEnabled,
} from 'lib/metasearch/metasearchDiscountContext'
import { isMetasearchPublicRateActive } from 'lib/metasearch/metasearchPublicRate'
import { getMetasearchDiscountAmount } from 'selectors/metasearchDiscountSelectors'
import { getGhaPriceMatchForProperty } from 'selectors/ghaPriceMatchSelectors'
import { useAppSelector } from './reduxHooks'
import useMetasearchPriceId from './useMetasearchPriceId'
import useMetasearchRuleId from './useMetasearchRuleId'
import { serialiseOccupancy } from 'lib/search/occupancy'

interface Options {
  propertyId?: string
  checkIn?: string
  checkOut?: string
  rooms?: Array<App.Occupants>
  disabled?: boolean
}

export default function useMetasearchDiscount(
  price: number,
  options: Options = {},
): readonly [number, boolean, boolean] {
  const gpid = useMetasearchPriceId()
  // Read raw ruleId internally (like gpid) so the read-side key matches the write-side key without
  // threading rateRuleId through every caller of this hook.
  const rateRuleId = useMetasearchRuleId()
  const enabled = isMetasearchDiscountEnabled()
  // A session switched to the public rate (GHA-404) is never given the discount, so never show it.
  const isPublicRateOnly = useAppSelector(state => isMetasearchPublicRateActive(state.marketing.metasearchPublicRateMarkedAt))
  // fetchMetasearchDiscount never fetches for a price-matched property, so treating the discount as
  // enabled here would wait forever for a result.
  const isPriceMatched = useAppSelector(state => (
    !!options.propertyId && !!getGhaPriceMatchForProperty(state, options.propertyId)
  ))
  const discountEnabled = enabled && !options.disabled && !isPublicRateOnly && !isPriceMatched
  const occupancy = options.rooms ? serialiseOccupancy(options.rooms) : undefined
  // Same either-identifier gate as the write side (hasMetasearchDiscountContext): a tokenless
  // landing is resolved from ruleId, and requiring gpid here would hide the stored result.
  const requestKey = discountEnabled && (gpid || rateRuleId) && options.propertyId && options.checkIn && options.checkOut && occupancy?.length && price > 0 ?
      buildMetasearchDiscountRequestKey({
        token: gpid,
        propertyId: options.propertyId,
        checkIn: options.checkIn,
        checkOut: options.checkOut,
        occupancy,
        price,
        rateRuleId,
      }) :
    undefined
  const selectorContext = {
    enabled: discountEnabled,
    requestKey,
  }

  const discountAmount = useAppSelector(state => getMetasearchDiscountAmount(state, selectorContext))
  const isDiscountFetching = useAppSelector(state => !!state.marketing.metasearchDiscount.fetching)
  const discountRequestKey = useAppSelector(state => state.marketing.metasearchDiscount.requestKey)
  const fetching = !!requestKey && isDiscountFetching
  const settled = !requestKey || (discountRequestKey === requestKey && !fetching)

  return [discountAmount, fetching, settled] as const
}
